import { Form, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { getLanguage, getNavPath } from "auth-provider";
import { useCallback, useEffect, useState } from "react";
import { LabelType } from "screens/register/page-header";
import {
  BtnText,
  formPlaceholder,
  VerifyTipConfig,
} from "screens/register/contant";
import { DrawForm } from "./form-utils";
import { useNavigate } from "react-router-dom";
import { getErrorMsg, showErrorMsg, useHttp } from "utils/http";
import {
  AccessTokenQueryKey,
  RegisterResQueryKey,
  UserPhoneQueryKey,
} from "utils/oauth";
import {
  AUTH_API_URL,
  checkAndSetRestaurantInfo,
  checkRepeatRegister,
  DEVICE_TYPE_KEY,
  PROJECT_CLIENT_ID,
  sendMessageToParent,
  StorageMobile,
  useProjectModal,
} from "screens/register/util";
import { useQueryClient } from "react-query";
import { NamePhone, AuthType } from "types/oauth";
import { useAuth } from "context/auth-context";
import { defaultHeaders, getHeaders } from "utils/request";
import "../register/customize.css";
import { SubmitButton, TipMsg } from "./join-member-form";
import {
  MIX_PANEL_EVENT,
  trackMixPanel,
  useTrackCommonData,
} from "mixpanel/mixpanelTrackUtil";
import { getBrowserType, slicePhoneNumber } from "utils";
import { useUrlQueryParam } from "utils/url";
import { saveConsent } from "@fantuaneats/user-common";
import { useLoyaltyMemberService } from "hook/useLoyaltyMemberServiceHook";



export const VerifyPhoneForm = (props: {
  setHeight: (height: string) => void;
}) => {
  const { restaurantCode, restaurant: restaurantInfo } = useAuth();
  const { open, originFrom, close } = useProjectModal();
  const [formData, setFormData] = useState({ phone: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const { registerMember } = useLoyaltyMemberService();

  const client = useHttp();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const lang = getLanguage() as LabelType;

  const [btnDisabled, setIsEnable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const trackCommonData = useTrackCommonData();

  const [{ source = "" }] = useUrlQueryParam(["source"]);

  const setParentHeight = useCallback(
    (h: string) => {
      props.setHeight(h);
    },
    [props]
  );

  const jumpToRestaurant = () => {
    const navPath = getNavPath("restaurant");
    sendMessageToParent({ type: "restaurant", data: null }, source);
    navigate(navPath, { replace: true });
  };

  const handleChange = (e: any) => {
    const inputVal = e.target.value.trim();
    setFormData({ ...formData, phone: inputVal });
    setIsEnable(!inputVal);
    setErrorMsg("");
  };

  const errorMsgNotify = (description: string) => {
    if (lang === "en") {
      setErrorMsg(description);
      return;
    }
    // 中文提示
    if (description === VerifyTipConfig.verifyError.en) {
      setErrorMsg(VerifyTipConfig.verifyError.zh);
    } else {
      setErrorMsg(description);
    }
  };

  const trackData = (
    type: "send" | "success" | "failed",
    option?: { [key in string]: any }
  ) => {
    switch (type) {
      case "send":
        if (originFrom === "register") {
          trackMixPanel(
            MIX_PANEL_EVENT.REGISTER_SEND_VERIFY_CODE,
            trackCommonData
          );
          sendMessageToParent(
            {
              type: "mixpanel",
              data: { eventName: MIX_PANEL_EVENT.REGISTER_SEND_VERIFY_CODE },
            },
            trackCommonData.source
          );
        } else if (originFrom === "login") {
          trackMixPanel(
            MIX_PANEL_EVENT.LOGIN_SEND_VERIFY_CODE,
            trackCommonData
          );
        }
        break;
      case "success":
        if (originFrom === "register") {
          trackMixPanel(
            MIX_PANEL_EVENT.REGISTER_SEND_VERIFY_CODE_SUCCESS,
            trackCommonData
          );
        } else if (originFrom === "login") {
          trackMixPanel(
            MIX_PANEL_EVENT.LOGIN_SEND_VERIFY_CODE_SUCCESS,
            trackCommonData
          );
        }
        break;
      case "failed":
        if (originFrom === "register") {
          trackMixPanel(MIX_PANEL_EVENT.REGISTER_SEND_VERIFY_CODE_FAILED, {
            ...trackCommonData,
            ...option,
          });
        } else if (originFrom === "login") {
          trackMixPanel(MIX_PANEL_EVENT.LOGIN_SEND_VERIFY_CODE_FAILED, {
            ...trackCommonData,
            ...option,
          });
        }
        break;
      default:
        break;
    }
  };

  const onFinish = (values: any) => {
    if (btnDisabled || isLoading) return;
    setErrorMsg("");
    const phoneNumber = StorageMobile("get");
    // 没有手机号不处理
    if (!phoneNumber || !values.phone) {
      return;
    }
    trackData("send");
    setIsLoading(true);
    client(`${AUTH_API_URL}/oauth/token`, {
      method: "post",
      headers: defaultHeaders,
      params: { locale: lang },
      data: {
        phone_number: phoneNumber,
        verification_code: values.phone,
        grant_type: "password",
      },
    })
      .then((data) => {
        trackData("success");
        const previousItems = queryClient.getQueryData(AccessTokenQueryKey);
        if (previousItems) {
          queryClient.invalidateQueries(AccessTokenQueryKey);
        }
        queryClient.setQueryData(AccessTokenQueryKey, data);
        localStorage.setItem(AccessTokenQueryKey, JSON.stringify(data));
        // 从register过来先注册再跳转；否则直接跳转
        saveConsent({
          url: AUTH_API_URL,
          lang,
          headers: getHeaders({
            "client-id": PROJECT_CLIENT_ID,
          }),
          flag: true,
        });
        if (originFrom === "register") {
          register();
          return;
        }
        // 登录成功
        setIsLoading(false);
        close();
        jumpToRestaurant();
      })
      .catch((e) => {
        // 登录或注册失败
        setIsLoading(false);
        const msg = getErrorMsg(e);
        errorMsgNotify(msg);
        trackData("failed", {
          errorMsg: msg,
          mNo: slicePhoneNumber(phoneNumber),
          errorApi: "oauth/token",
        });
      });
  };

  const register = () => {
    const namePhone = queryClient.getQueryData(UserPhoneQueryKey) as NamePhone;
    const accessTokenData = queryClient.getQueryData(
      AccessTokenQueryKey
    ) as AuthType;
    if (!namePhone?.name || !namePhone?.phone) {
      message.error(VerifyTipConfig.registerError[lang]);
      trackMixPanel(
        MIX_PANEL_EVENT.REGISTER_CLOSE_VERIFY_CODE,
        trackCommonData
      );
      open("join", "");
      return;
    }
    const deviceSystem =
      localStorage.getItem(DEVICE_TYPE_KEY) || getBrowserType().osName;
    trackMixPanel(MIX_PANEL_EVENT.REGISTER_SEND_REQUEST, trackCommonData);
    // 注册餐厅会员
    // client(`${APP_API_URL}/api/${lang}/v1/restaurant_members`, {
    //   method: "post",
    //   headers: {
    //     Authorization: `${accessTokenData?.token_type} ${accessTokenData?.access_token}`,
    //     ...defaultHeaders,
    //   },
    //   data: {
    //     name: namePhone.name,
    //     phone_number: namePhone.phone,
    //     email: namePhone.email || '',
    //     birthday: namePhone.birthday || '',
    //     restaurant_id: restaurantInfo?.data?.id,
    //     device_system: deviceSystem,
    //     language: lang,
    //     source: sourceMap.get(source) || "loyalty_web",
    //   },
    // })
    registerMember(lang, accessTokenData || { token_type: '', access_token: '' }, {
      name: namePhone.name,
      phone: namePhone.phone,
      email: namePhone.email,
      birthday: namePhone.birthday,
    }, deviceSystem, source)
      .then((data) => {
        trackMixPanel(
          MIX_PANEL_EVENT.REGISTER_SEND_REQUEST_SUCCESS,
          trackCommonData
        );
        sendMessageToParent(
          {
            type: "mixpanel",
            data: {
              eventName: MIX_PANEL_EVENT.REGISTER_SEND_REQUEST_SUCCESS,
              eventProps: { mNo: slicePhoneNumber(namePhone.phone) },
            },
          },
          trackCommonData.source
        );
        setIsLoading(false);
        queryClient.setQueryData(RegisterResQueryKey, data);
        localStorage.setItem(RegisterResQueryKey, JSON.stringify(data));
        // 登录成功后，将 用户名、手机号、token等信息，缓存到本地，刷新时用
        const usePhoneData = queryClient.getQueryData(UserPhoneQueryKey);
        const accessTokenData = queryClient.getQueryData(AccessTokenQueryKey);
        localStorage.setItem(UserPhoneQueryKey, JSON.stringify(usePhoneData));
        localStorage.setItem(
          AccessTokenQueryKey,
          JSON.stringify(accessTokenData)
        );
        // 跳转到详情页面
        checkAndSetRestaurantInfo("set", namePhone.phone, restaurantCode);
        close();
        // 注册成功
        jumpToRestaurant();
      })
      .catch((error) => {
        setIsLoading(false);
        close();
        if (checkRepeatRegister(error)) {
          jumpToRestaurant();
        } else {
          // 注册失败时，重新退回到姓名电话部分
          showErrorMsg(error, () => {
            open("join", "");
          });
        }
        const msg = getErrorMsg(error);
        trackMixPanel(MIX_PANEL_EVENT.REGISTER_SEND_REQUEST_FAILED, {
          ...trackCommonData,
          errorMsg: msg,
          mNo: slicePhoneNumber(namePhone.phone),
          restaurant_id: restaurantInfo?.data?.id,
          errorApi: `api/{lang}/v1/restaurant_members`,
        });
      });
  };

  const [formRef] = useForm();
  useEffect(() => {
    const ins = formRef.getFieldInstance("phone");
    ins.focus();
  }, [formRef]);

  useEffect(() => {
    if (errorMsg) {
      setParentHeight("25rem");
    } else {
      setParentHeight("21.4rem");
    }
  }, [errorMsg, setParentHeight]);
  return (
    <DrawForm
      form={formRef}
      layout={"vertical"}
      onFinish={onFinish}
      initialValues={formData}
    >
      {/* 验证码 */}
      <Form.Item style={{ marginBottom: "1.6rem" }} name={"phone"}>
        <input
          type={"number"}
          className={"iptClasses commonIptClass"}
          placeholder={formPlaceholder.verify.phone[lang]}
          autoComplete={"off"}
          maxLength={10}
          onChange={(e) => handleChange(e)}
        ></input>
      </Form.Item>
      {errorMsg && <TipMsg>{errorMsg}</TipMsg>}
      <Form.Item style={{ marginTop: "1.6rem" }}>
        <SubmitButton
          type={"submit"}
          isDisabled={!!isLoading || btnDisabled}
          lang={lang}
          style={{ width: "100%" }}
        >
          {!isLoading
            ? formPlaceholder.verify.btn[lang]
            : BtnText.btnSending[lang]}
        </SubmitButton>
      </Form.Item>
    </DrawForm>
  );
};
